import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Separator } from '../components/ui/separator';
import { BookOpen, Microscope, BrainCircuit, Trash2, Calendar, Clock, AlertCircle } from 'lucide-react';
import Timer from '../components/Timer';
import { toast } from 'sonner';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '../components/ui/alert-dialog';

export default function TopicDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [questionsTotal, setQuestionsTotal] = useState('');
  const [questionsWrong, setQuestionsWrong] = useState('');
  const [errorTopics, setErrorTopics] = useState('');

  useEffect(() => {
    // Validar ID antes de fazer fetch
    if (!id || id.includes('-review-') || id === 'undefined' || id === 'null') {
      setError('ID do tema inválido');
      setLoading(false);
      toast.error('ID do tema inválido');
      return;
    }
    fetchTopic();
  }, [id]);

  const fetchTopic = async () => {
    try {
      const response = await api.get(`/topics/${id}`);
      if (!response.data) {
        throw new Error('Tema não encontrado');
      }
      setTopic(response.data);
      setQuestionsTotal(response.data.questions_total?.toString() || '0');
      setQuestionsWrong(response.data.questions_wrong?.toString() || '0');
      setErrorTopics(response.data.error_topics?.join(', ') || '');
      setError(null);
    } catch (error) {
      console.error('Error fetching topic:', error);
      setError('Erro ao carregar tema');
      toast.error('Erro ao carregar tema');
    } finally {
      setLoading(false);
    }
  };

  const handleStageToggle = async (stage, currentValue) => {
    try {
      const update = { [stage]: !currentValue };
      const response = await api.patch(`/topics/${id}`, update);
      setTopic(response.data);
      toast.success('Progresso atualizado!');
    } catch (error) {
      console.error('Error updating topic:', error);
      toast.error('Erro ao atualizar progresso');
    }
  };

  const handleQuestionsUpdate = async () => {
    try {
      const total = parseInt(questionsTotal) || 0;
      const wrong = parseInt(questionsWrong) || 0;
      
      if (wrong > total) {
        toast.error('Questões erradas não podem ser maior que o total');
        return;
      }

      const update = {
        questions_total: total,
        questions_wrong: wrong,
        error_topics: errorTopics.split(',').map(t => t.trim()).filter(t => t)
      };

      const response = await api.patch(`/topics/${id}`, update);
      setTopic(response.data);
      toast.success('Questões atualizadas!');
    } catch (error) {
      console.error('Error updating questions:', error);
      toast.error('Erro ao atualizar questões');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/topics/${id}`);
      toast.success('Tema excluído com sucesso!');
      navigate('/topics');
    } catch (error) {
      console.error('Error deleting topic:', error);
      toast.error('Erro ao excluir tema');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  // Tela de erro amigável
  if (error || !topic) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="w-full max-w-md mx-4">
          <CardContent className="py-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Tema não encontrado</h2>
            <p className="text-muted-foreground mb-6">
              {error || 'O tema solicitado não existe ou foi removido.'}
            </p>
            <div className="space-y-2">
              <Button onClick={() => navigate('/calendar')} className="w-full">
                Voltar ao Calendário
              </Button>
              <Button variant="outline" onClick={() => navigate('/topics')} className="w-full">
                Ver Todos os Temas
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-container" data-testid="topic-detail-page">
      <div className="max-w-md mx-auto px-6 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight mb-4">{topic.title}</h1>
          <div className="flex items-center gap-4 text-sm text-slate-600">
            <span className="px-3 py-1 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-sm font-medium">
              {topic.specialty || 'Outros'}
            </span>
            <div className="flex items-center gap-1">
              <Calendar className="w-4 h-4" />
              {format(parseISO(topic.scheduled_date), "dd 'de' MMMM, yyyy", { locale: ptBR })}
            </div>
          </div>
        </div>

        <div className="mb-6">
          <Timer topicId={id} topicTitle={topic.title} onSessionComplete={() => fetchTopic()} />
        </div>

        <Card className="mb-6 shadow-sm" data-testid="study-stages-card">
          <CardHeader>
            <CardTitle className="text-xl">Ciclo de Estudo</CardTitle>
            {/* Indicador de status do estudo */}
            {(() => {
              const completedCount = [topic.reading_done, topic.deep_dive_done, topic.questions_done].filter(Boolean).length;
              if (completedCount === 0) {
                return (
                  <div className="flex items-center gap-2 mt-2 text-slate-500">
                    <div className="w-2 h-2 rounded-full bg-slate-400"></div>
                    <span className="text-sm">Não iniciado</span>
                  </div>
                );
              } else if (completedCount === 3) {
                return (
                  <div className="flex items-center gap-2 mt-2 text-green-600">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <span className="text-sm font-medium">Estudo completo</span>
                  </div>
                );
              } else {
                return (
                  <div className="flex items-center gap-2 mt-2 text-amber-600">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm font-medium">Estudo incompleto ({completedCount}/3)</span>
                  </div>
                );
              }
            })()}
          </CardHeader>
          <CardContent className="space-y-4">
            <div 
              className="flex items-center gap-3 p-4 rounded-lg hover:bg-accent transition-colors cursor-pointer"
              onClick={() => handleStageToggle('reading_done', topic.reading_done)}
            >
              <Checkbox checked={topic.reading_done} data-testid="reading-checkbox" />
              <BookOpen className="w-5 h-5 text-brand" />
              <span className={`flex-1 font-medium ${topic.reading_done ? 'line-through text-muted-foreground' : ''}`}>
                Leitura Rápida
              </span>
            </div>

            <div 
              className="flex items-center gap-3 p-4 rounded-lg hover:bg-accent transition-colors cursor-pointer"
              onClick={() => handleStageToggle('deep_dive_done', topic.deep_dive_done)}
            >
              <Checkbox checked={topic.deep_dive_done} data-testid="deepdive-checkbox" />
              <Microscope className="w-5 h-5 text-brand" />
              <span className={`flex-1 font-medium ${topic.deep_dive_done ? 'line-through text-muted-foreground' : ''}`}>
                Aprofundamento
              </span>
            </div>

            <div 
              className="flex items-center gap-3 p-4 rounded-lg hover:bg-accent transition-colors cursor-pointer"
              onClick={() => handleStageToggle('questions_done', topic.questions_done)}
            >
              <Checkbox checked={topic.questions_done} data-testid="questions-checkbox" />
              <BrainCircuit className="w-5 h-5 text-brand" />
              <span className={`flex-1 font-medium ${topic.questions_done ? 'line-through text-muted-foreground' : ''}`}>
                Questões
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-6 shadow-sm" data-testid="questions-card">
          <CardHeader>
            <CardTitle className="text-xl">Gestão de Questões</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="questionsTotal">Questões Realizadas</Label>
                <Input
                  id="questionsTotal"
                  type="number"
                  min="0"
                  value={questionsTotal}
                  onChange={(e) => setQuestionsTotal(e.target.value)}
                  data-testid="questions-total-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="questionsWrong">Questões Erradas</Label>
                <Input
                  id="questionsWrong"
                  type="number"
                  min="0"
                  value={questionsWrong}
                  onChange={(e) => setQuestionsWrong(e.target.value)}
                  data-testid="questions-wrong-input"
                />
              </div>
            </div>

            {topic.questions_total > 0 && (
              <div className="p-4 bg-brand/5 rounded-lg border border-brand/20">
                <div className="flex justify-between items-center">
                  <span className="text-sm font-medium">Taxa de Acerto</span>
                  <span className="text-2xl font-bold text-brand" data-testid="correct-rate-display">
                    {topic.correct_rate}%
                  </span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="errorTopics">Assuntos das Questões Erradas</Label>
              <Textarea
                id="errorTopics"
                placeholder="Ex: ICC descompensada, Choque cardiogênico"
                value={errorTopics}
                onChange={(e) => setErrorTopics(e.target.value)}
                rows={3}
                data-testid="error-topics-input"
              />
              <p className="text-xs text-muted-foreground">Separe os assuntos por vírgula</p>
            </div>

            <Button 
              onClick={handleQuestionsUpdate} 
              className="w-full"
              data-testid="update-questions-button"
            >
              Atualizar Questões
            </Button>
          </CardContent>
        </Card>

        {topic.review_dates && topic.review_dates.length > 0 && (
          <Card className="mb-6 shadow-sm" data-testid="review-dates-card">
            <CardHeader>
              <CardTitle className="text-xl">Datas de Revisão</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {topic.review_dates.map((date, index) => (
                  <div key={index} className="flex items-center justify-between p-3 rounded-lg bg-accent/50">
                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4 text-brand" />
                      <span className="font-medium">Revisão {['24h', '7d', '15d', '30d'][index]}</span>
                    </div>
                    <span className="text-sm text-muted-foreground">
                      {format(parseISO(date), "dd/MM/yyyy", { locale: ptBR })}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="destructive" className="w-full" data-testid="delete-topic-button">
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir Tema
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Tem certeza?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta ação não pode ser desfeita. O tema será permanentemente excluído.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="cancel-delete-button">Cancelar</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} data-testid="confirm-delete-button">
                Excluir
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}
